import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes, css } from 'styled-components';
import { Participant, DeckType } from '../../../domain/entities/Room';
import { TokenSet } from '../../styles/tokens';
import { useLanguage } from '../../contexts/LanguageContext';
import { getCardLabel } from '../../i18n/cardLabels';

/* ── throwable emojis ───────────────────────────────────────────── */

export const THROW_EMOJIS = ['🍅', '🥊', '💣', '🎯', '⚡', '🐟', '🥕', '🌶️', '🧊', '💀'];

/* ── static animations ──────────────────────────────────────────── */

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.35; }
`;

const popIn = keyframes`
  0%   { transform: scale(0.5); opacity: 0; }
  60%  { transform: scale(1.2); }
  100% { transform: scale(1);   opacity: 1; }
`;

const floatUp = keyframes`
  0%   { transform: translateY(0)     scale(1);   opacity: 1; }
  40%  { transform: translateY(-30px) scale(1.4); opacity: 1; }
  100% { transform: translateY(-90px) scale(0.7); opacity: 0; }
`;

/* ── layout ──────────────────────────────────────────────────────── */

const RoomWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 4px 0;
`;

const SeatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
  width: 100%;
`;

const TableStrip = styled.div<{ tokens: TokenSet }>`
  width: 100%;
  max-width: 680px;
  background: ${p => p.tokens.tableFelt};
  border: 4px solid ${p => p.tokens.tableEdge};
  border-radius: 18px;
  padding: 12px 24px;
  text-align: center;
  color: rgba(255,255,255,0.85);
  font-size: 14px;
  font-weight: 600;
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 24px rgba(0,0,0,0.2), inset 0 2px 8px rgba(0,0,0,0.2);
`;

const TablePlaceholder = styled.span`
  opacity: 0.55;
  font-style: italic;
  font-weight: 500;
`;

/* ── seat card ───────────────────────────────────────────────────── */

const SeatCard = styled.div<{ voted: boolean; tokens: TokenSet; clickable: boolean; isMe: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: ${p => p.tokens.bgSurface};
  border: 2px solid ${p => p.voted ? p.tokens.primary : p.tokens.border};
  border-radius: 16px;
  padding: 8px 8px;
  min-width: 66px;
  max-width: 82px;
  transition: border-color 0.3s, box-shadow 0.3s, transform 0.15s;
  box-shadow: ${p => p.voted
    ? `0 0 0 3px ${p.tokens.primary}33, 0 4px 12px ${p.tokens.shadow}`
    : `0 2px 8px ${p.tokens.shadow}`};
  cursor: ${p => p.clickable ? 'pointer' : 'default'};
  ${p => p.isMe && css`
    outline: 2px dashed ${p.tokens.primary}55;
    outline-offset: 3px;
  `}
  &:hover { ${p => p.clickable && css`transform: translateY(-2px);`} }
  @media (max-width: 480px) {
    padding: 6px 6px;
    min-width: 56px;
  }
`;

const AnimalEmoji = styled.div`
  font-size: 22px;
  line-height: 1;
  @media (max-width: 480px) { font-size: 18px; }
`;

const SeatName = styled.div<{ tokens: TokenSet }>`
  font-size: 11px;
  font-weight: 700;
  color: ${p => p.tokens.text};
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
`;

/* ── vote card ───────────────────────────────────────────────────── */

const VoteCard = styled.div<{ voted: boolean; revealed: boolean; tokens: TokenSet }>`
  width: 28px;
  height: 40px;
  border-radius: 7px;
  border: 2px solid ${p => {
    if (p.revealed && p.voted) return p.tokens.primary;
    if (p.voted) return p.tokens.primaryLight;
    return p.tokens.border;
  }};
  background: ${p => {
    if (p.revealed && p.voted) return p.tokens.primary;
    if (p.voted) return p.tokens.cardBack;
    return p.tokens.bgElevated;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 900;
  color: ${p => (p.revealed && p.voted) ? '#fff' : p.tokens.textFaint};
  transition: background 0.4s, border-color 0.4s, color 0.4s;
  ${p => p.revealed && p.voted && css`animation: ${popIn} 0.4s ease forwards;`}
  @media (max-width: 480px) {
    width: 24px;
    height: 34px;
    font-size: 12px;
  }
`;

const VotedBadge = styled.div<{ tokens: TokenSet }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${p => p.tokens.success};
  color: #fff;
  font-weight: 900;
  font-size: 16px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px ${p => p.tokens.shadow};
  pointer-events: none;
  z-index: 5;
  animation: ${popIn} 0.35s ease forwards;
`;

const ThinkingDots = styled.div<{ tokens: TokenSet }>`
  font-size: 13px;
  color: ${p => p.tokens.textFaint};
  animation: ${pulse} 1.6s ease-in-out infinite;
  letter-spacing: 2px;
`;

/* ── reaction float (stays on target card) ──────────────────────── */

const FloatingEmoji = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  font-size: 28px;
  pointer-events: none;
  animation: ${floatUp} 2.5s ease-out forwards;
  z-index: 50;
`;

/* ── menu (portal, position: fixed) ─────────────────────────────── */

const Menu = styled.div<{ tokens: TokenSet }>`
  position: fixed;
  background: ${p => p.tokens.bgSurface};
  border: 1px solid ${p => p.tokens.border};
  border-radius: 14px;
  padding: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  width: 200px;
  z-index: 9999;
  box-shadow: 0 8px 32px rgba(0,0,0,0.35);
`;

const EmojiBtn = styled.button<{ tokens: TokenSet }>`
  background: ${p => p.tokens.bgElevated};
  border: 1px solid ${p => p.tokens.border};
  border-radius: 8px;
  width: 36px; height: 36px;
  font-size: 18px;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: transform 0.15s;
  &:hover { transform: scale(1.25); }
`;

const MenuLabel = styled.div<{ tokens: TokenSet }>`
  width: 100%;
  font-size: 11px;
  font-weight: 600;
  color: ${p => p.tokens.textMuted};
  text-align: center;
  margin-bottom: 4px;
`;

/* ── flying throw projectile (Web Animations API) ───────────────── */

interface FlyData {
  id: string;
  emoji: string;
  fromX: number;
  fromY: number;
  dx: number;
  dy: number;
}

const Projectile: React.FC<{ data: FlyData }> = ({ data }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { dx, dy } = data;
    const arcY = -Math.max(50, Math.sqrt(dx * dx + dy * dy) * 0.35);

    el.animate([
      { transform: `translate(0px,0px) rotate(0deg) scale(1)`,                         opacity: 1 },
      { transform: `translate(${dx*.45}px,${dy*.45+arcY}px) rotate(200deg) scale(1.2)`,opacity: 1, offset: 0.45 },
      { transform: `translate(${dx}px,${dy}px) rotate(360deg) scale(1)`,               opacity: 1, offset: 0.72 },
      { transform: `translate(${dx}px,${dy+18}px) rotate(415deg) scale(0.85)`,         opacity: 0.9, offset: 0.82 },
      { transform: `translate(${dx}px,${dy+44}px) rotate(450deg) scale(0.45)`,         opacity: 0.45, offset: 0.91 },
      { transform: `translate(${dx}px,${dy+68}px) rotate(470deg) scale(0.05)`,         opacity: 0 },
    ], { duration: 1100, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)', fill: 'forwards' });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return ReactDOM.createPortal(
    <div ref={ref} style={{
      position: 'fixed',
      fontSize: 28,
      pointerEvents: 'none',
      zIndex: 9998,
      left: data.fromX - 14,
      top: data.fromY - 14,
      transformOrigin: 'center',
    }}>
      {data.emoji}
    </div>,
    document.body
  );
};

/* ── props ───────────────────────────────────────────────────────── */

export interface ActiveThrow {
  id: string;
  fromUserId: string;
  toUserId: string;
  emoji: string;
}

interface ActiveReaction {
  id: string;
  userId: string;
  emoji: string;
}

interface PokerTableProps {
  participants: Record<string, Participant>;
  currentStory: string;
  revealed: boolean;
  tokens: TokenSet;
  currentUserId: string;
  activeThrows: ActiveThrow[];
  activeReactions: ActiveReaction[];
  onThrow: (toUserId: string, emoji: string) => void;
  onReact: (emoji: string) => void;
  deckType?: DeckType;
}

/* ── component ───────────────────────────────────────────────────── */

export const PokerTable: React.FC<PokerTableProps> = ({
  participants, currentStory, revealed, tokens,
  currentUserId, activeThrows, activeReactions, onThrow, onReact,
  deckType = 'fibonacci',
}) => {
  const { t } = useLanguage();
  const [menuTarget, setMenuTarget] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
  const [flyQueue, setFlyQueue] = useState<FlyData[]>([]);
  const seenIds = useRef(new Set<string>());

  const players = Object.values(participants);
  const half = Math.ceil(players.length / 2);
  const topRow = players.slice(0, half);
  const bottomRow = players.slice(half);

  // Detect new throw events, look up card positions, enqueue projectile
  useEffect(() => {
    activeThrows.forEach(t => {
      if (seenIds.current.has(t.id)) return;
      seenIds.current.add(t.id);

      const fromEl = document.querySelector<HTMLElement>(`[data-userid="${t.fromUserId}"]`);
      const toEl   = document.querySelector<HTMLElement>(`[data-userid="${t.toUserId}"]`);
      if (!fromEl || !toEl) return;

      const fr = fromEl.getBoundingClientRect();
      const tr = toEl.getBoundingClientRect();
      const fromX = fr.left + fr.width  / 2;
      const fromY = fr.top  + fr.height / 2;
      const dx = (tr.left + tr.width  / 2) - fromX;
      const dy = (tr.top  + tr.height / 2) - fromY;

      const fly: FlyData = { id: t.id, emoji: t.emoji, fromX, fromY, dx, dy };
      setFlyQueue(prev => [...prev, fly]);
      setTimeout(() => setFlyQueue(prev => prev.filter(f => f.id !== t.id)), 1400);
    });
  }, [activeThrows]);

  const handleCardClick = (userId: string, e: React.MouseEvent<HTMLDivElement>) => {
    if (menuTarget === userId) { setMenuTarget(null); return; }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const isMe = userId === currentUserId;
    const menuW = 200;
    const menuH = isMe ? 220 : 150;
    let x = rect.left + rect.width / 2 - menuW / 2;
    const y = rect.bottom + 8 + menuH > window.innerHeight
      ? rect.top - menuH - 8
      : rect.bottom + 8;
    x = Math.max(8, Math.min(x, window.innerWidth - menuW - 8));
    setMenuPos({ x, y });
    setMenuTarget(userId);
  };

  const handleEmojiClick = (userId: string, emoji: string) => {
    if (userId === currentUserId) {
      onReact(emoji);
    } else {
      onThrow(userId, emoji);
    }
    setMenuTarget(null);
  };

  const renderSeat = (p: Participant) => {
    const voted = !!p.vote;
    const isMe  = p.userId === currentUserId;
    const myReactions = activeReactions.filter(r => r.userId === p.userId);
    const titleText = isMe ? t.reactAs : t.throwAt(p.name.split(' ')[0]);

    return (
      <SeatCard
        key={p.userId}
        data-userid={p.userId}
        voted={voted}
        tokens={tokens}
        clickable={true}
        isMe={isMe}
        onClick={e => handleCardClick(p.userId, e)}
        title={titleText}
      >
        {voted && !revealed && (
          <VotedBadge tokens={tokens} role="img" aria-label={t.votedBadge}>✓</VotedBadge>
        )}

        {myReactions.map(r => <FloatingEmoji key={r.id}>{r.emoji}</FloatingEmoji>)}

        <AnimalEmoji>{p.animal || '👤'}</AnimalEmoji>
        <SeatName tokens={tokens}>{p.name.split(' ')[0]}</SeatName>
        <VoteCard voted={voted} revealed={revealed} tokens={tokens}>
          {revealed && p.vote
            ? getCardLabel(p.vote, deckType, t)
            : voted ? null : <ThinkingDots tokens={tokens}>···</ThinkingDots>
          }
        </VoteCard>
      </SeatCard>
    );
  };

  const menuPlayer = menuTarget ? players.find(p => p.userId === menuTarget) : null;
  const isMyMenu = menuPlayer?.userId === currentUserId;
  const menuEmojis: string[] = isMyMenu
    ? t.reactions.map(r => r.emoji)
    : THROW_EMOJIS;
  const menuLabel = isMyMenu
    ? t.reactAs
    : (menuPlayer ? t.throwAt(menuPlayer.name.split(' ')[0]) : '');

  return (
    <RoomWrapper onClick={() => setMenuTarget(null)}>
      <SeatsRow onClick={e => e.stopPropagation()}>{topRow.map(renderSeat)}</SeatsRow>

      <TableStrip tokens={tokens}>
        {currentStory ? currentStory : <TablePlaceholder>{t.waitingStory}</TablePlaceholder>}
      </TableStrip>

      {bottomRow.length > 0 && (
        <SeatsRow onClick={e => e.stopPropagation()}>{bottomRow.map(renderSeat)}</SeatsRow>
      )}

      {/* Flying projectiles */}
      {flyQueue.map(f => <Projectile key={f.id} data={f} />)}

      {/* Emoji picker menu */}
      {menuPlayer && ReactDOM.createPortal(
        <Menu tokens={tokens} style={{ left: menuPos.x, top: menuPos.y }}
          onClick={e => e.stopPropagation()}>
          <MenuLabel tokens={tokens}>{menuLabel}</MenuLabel>
          {menuEmojis.map(emoji => (
            <EmojiBtn key={emoji} tokens={tokens} onClick={() => handleEmojiClick(menuPlayer.userId, emoji)}>
              {emoji}
            </EmojiBtn>
          ))}
        </Menu>,
        document.body
      )}
    </RoomWrapper>
  );
};
